import { forwardRef, useCallback, type ButtonHTMLAttributes, type MouseEvent } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useSfx } from '@/lib/use-sfx';
import { cn } from '@/lib/cn';

/**
 * The "3D press" button from the design system: a solid face with a darker bottom
 * edge that collapses on press. Height 52 at default size keeps it comfortably
 * above the 44px touch-target minimum.
 */
const button = cva(
  [
    'relative inline-flex items-center justify-center gap-2 select-none',
    'font-bold uppercase tracking-wide',
    'rounded-xl press',
    'active:press-active',
    'disabled:opacity-45 disabled:cursor-not-allowed disabled:active:translate-y-0',
    'disabled:active:border-b-[length:var(--press-depth)]',
    'transition-[filter,transform,border-bottom-width]',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand text-on-brand border-b-brand-shade hover:brightness-105',
        success: 'bg-success text-on-success border-b-success-shade hover:brightness-105',
        danger: 'bg-danger text-on-danger border-b-danger-shade hover:brightness-105',
        neutral:
          'bg-surface text-text border-b-line-strong border-2 border-line hover:bg-surface-2',
        ghost: 'bg-transparent text-text-2 border-b-0 hover:bg-bg-2 normal-case font-semibold',
      },
      size: {
        sm: 'h-10 px-4 text-sm',
        md: 'h-13 px-6 text-base',
        lg: 'h-14 px-8 text-lg',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  /** Renders a spinner and blocks interaction without changing layout width. */
  loading?: boolean;
  /**
   * Suppresses the tap sound. For a button whose own moment is louder — the
   * answer tiles, which play `pop` at their own pitch, or a chest, which has a
   * whole choreography — so the press does not click underneath it.
   */
  silent?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    block,
    loading = false,
    silent = false,
    disabled,
    children,
    onClick,
    ...rest
  },
  ref,
) {
  const sfx = useSfx();

  // Every press clicks, from one place, so a new screen cannot forget to.
  const press = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!silent) sfx.tap();
      onClick?.(event);
    },
    [silent, sfx, onClick],
  );

  return (
    <button
      ref={ref}
      className={cn(button({ variant, size, block }), className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      onClick={press}
      {...rest}
    >
      {loading ? (
        <>
          <span
            aria-hidden
            className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
          <span className="sr-only">Loading</span>
        </>
      ) : null}
      {children}
    </button>
  );
});
